<template>
  <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar pt-8 pr-8 pb-32 pl-8">
    <div class="max-w-[1600px] mx-auto space-y-6">
      <div class="flex items-center gap-2 pb-2">
        <div class="flex gap-2 overflow-x-auto custom-scrollbar min-w-0 flex-1">
          <button
            v-for="f in filters"
            :key="f.value"
            type="button"
            class="px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors flex items-center gap-1.5 shrink-0"
            :class="activeFilter === f.value
              ? 'bg-[#1c4848] text-white shadow-md'
              : 'bg-white border border-[#1c4848]/10 text-[#1c4848]/60 hover:bg-[#fafbfc] hover:text-[#1c4848]'"
            @click="applyFilter(f.value)"
          >
            <span v-if="f.dot" class="w-1.5 h-1.5 rounded-full shrink-0" :class="f.dotClass" />
            <span>{{ f.label }}</span>
          </button>
        </div>
        <div class="shrink-0">
          <LanguageFilterDropdown v-model="selectedLanguageId" />
        </div>
      </div>
      <div v-if="booksStore.booksLoading" class="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <BookCardSkeleton v-for="i in 4" :key="i" />
      </div>
      <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <BookCard
          v-for="book in filteredBooks"
          :key="book.id ?? book"
          :book="book"
          @edit="$emit('edit', book)"
          @duplicated="onBookDuplicated"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useBooksStore } from '@/store/books/index.js';
import BookCard from '@/components/BookCard.vue';
import BookCardSkeleton from '@/components/BookCardSkeleton.vue';
import LanguageFilterDropdown from '@/components/LanguageFilterDropdown.vue';

const booksStore = useBooksStore();
await booksStore.fetchBooks({});

const props = defineProps({ searchQuery: { type: String, default: '' } });
defineEmits(['edit']);
const activeFilter = ref('all');
const selectedLanguageId = ref('');

const filters = [
  { value: 'all', label: 'All Books' },
  { value: 'published', label: 'Published', dot: true, dotClass: 'bg-emerald-500' },
  { value: 'pending', label: 'Pending Approval', dot: true, dotClass: 'bg-amber-500' },
  { value: 'adults', label: 'Adults' },
  { value: 'kids', label: 'Kids' },
  { value: 'premium', label: 'Premium' },
];

function apiFiltersFor(value) {
  const f = {};
  if (value === 'published') f.published = true;
  else if (value === 'pending') f.approval_status = 'PENDING';
  else if (value === 'adults') f.audience = 'adult';
  else if (value === 'kids') f.audience = 'kids';
  else if (value === 'premium') f.premium = true;
  if ((props.searchQuery || '').trim()) f.name = props.searchQuery.trim();
  if (selectedLanguageId.value) f.language_id = selectedLanguageId.value;
  return f;
}

const filteredBooks = computed(() => booksStore.books || []);

async function applyFilter(value) {
  activeFilter.value = value;
  const apiFilters = value === 'all' ? {} : apiFiltersFor(value);
  if (value === 'all' && (props.searchQuery || '').trim()) apiFilters.name = props.searchQuery.trim();
  if (value === 'all' && selectedLanguageId.value) apiFilters.language_id = selectedLanguageId.value;
  await booksStore.fetchBooks(apiFilters);
}

async function onBookDuplicated() {
  await applyFilter(activeFilter.value);
}

watch(selectedLanguageId, () => applyFilter(activeFilter.value));

let searchDebounce = null;
watch(() => props.searchQuery, () => {
  if (searchDebounce) clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => applyFilter(activeFilter.value), 300);
});

defineExpose({ applyFilter });
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(28, 72, 72, 0.1); border-radius: 20px; }
.custom-scrollbar:hover::-webkit-scrollbar-thumb { background-color: rgba(28, 72, 72, 0.2); }
</style>
